import React from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate } from '../../utils/formatters';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const DEFAULT_EVENT_COLOR = '#B47330';

/**
 * Month calendar grid rendered with date-fns.
 * Presentational only — receives events and reports navigation upward.
 */
const MonthGrid = ({ events = [], currentMonth, onMonthChange, onEventClick }) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // The grid always shows full weeks, so it spills into the neighbouring months.
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart, { locale: ptBR }),
    end: endOfWeek(monthEnd, { locale: ptBR }),
  });

  const eventsForDay = (day) =>
    events.filter((event) => {
      if (!event.startsAt) return false;
      try {
        return isSameDay(parseISO(event.startsAt), day);
      } catch {
        return false;
      }
    });

  const monthLabel = formatDate(currentMonth, "MMMM 'de' yyyy");

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Month navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <button
          type="button"
          onClick={() => onMonthChange(subMonths(currentMonth, 1))}
          aria-label="Mês anterior"
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-brand-brown" />
        </button>

        <h2 className="text-lg font-semibold text-brand-brown capitalize">{monthLabel}</h2>

        <button
          type="button"
          onClick={() => onMonthChange(addMonths(currentMonth, 1))}
          aria-label="Próximo mês"
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="h-5 w-5 text-brand-brown" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {WEEKDAYS.map((weekday) => (
          <div
            key={weekday}
            className="py-2 text-center text-xs font-semibold text-gray-500 uppercase"
          >
            {weekday}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayEvents = eventsForDay(day);
          const inMonth = isSameMonth(day, currentMonth);

          return (
            <div
              key={day.toISOString()}
              className={`min-h-[100px] border-b border-r border-gray-200 p-1.5 ${
                inMonth ? 'bg-white' : 'bg-gray-50'
              }`}
            >
              <div className="flex justify-end">
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    isToday(day)
                      ? 'bg-brand-gold font-bold text-white'
                      : inMonth
                        ? 'text-gray-700'
                        : 'text-gray-400'
                  }`}
                >
                  {formatDate(day, 'd')}
                </span>
              </div>

              <div className="mt-1 space-y-1">
                {dayEvents.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => onEventClick && onEventClick(event)}
                    title={event.title}
                    className="block w-full truncate rounded px-1.5 py-1 text-left text-xs font-medium text-white transition-opacity hover:opacity-80"
                    style={{ backgroundColor: event.color || DEFAULT_EVENT_COLOR }}
                  >
                    {!event.allDay && (
                      <span className="mr-1 opacity-90">{formatDate(event.startsAt, 'HH:mm')}</span>
                    )}
                    {event.title}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGrid;
